import React from 'react';

/**
 * ChartTooltip — a shared, Key-Metrics-styled hover card for the charts
 * (Throughput Timeline + Daily Overview). Flat, sharp-cornered, hairline-bordered
 * (matches the date-picker popover and the instrument-cluster KPI tiles): a
 * tracked uppercase header, then metric rows of [colour dot · micro-label →
 * big tabular value · note], and an optional emphasised footer row.
 *
 * props:
 *   title   — header string (the hovered node's time / day)
 *   rows    — [{ label, value, note?, dot?: hex, dotClass?: tailwind }]
 *   footer  — optional { label, value } rendered under a hairline
 */
const ChartTooltip = ({ title, rows = [], footer }) => (
  <div className="min-w-[168px] rounded-sm border border-[#cacacb] dark:border-white/15 bg-white dark:bg-[#0E0E0F] px-3.5 py-2.5">
    <div className="mb-2 border-b border-[#e5e5e5] dark:border-white/[0.08] pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#9e9ea0] dark:text-white/45">
      {title}
    </div>

    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between gap-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[#707072] dark:text-white/45">
            {(r.dot || r.dotClass) && (
              <span
                className={`h-2 w-2 rounded-sm ${r.dotClass || ''}`}
                style={r.dot ? { backgroundColor: r.dot } : undefined}
              />
            )}
            {r.label}
          </span>
          <span className="text-[13px] font-black leading-none tabular-nums text-[#111111] dark:text-white">
            {r.value}
            {r.note != null && (
              <span className="ml-1.5 text-[10px] font-bold text-[#707072] dark:text-white/45">{r.note}</span>
            )}
          </span>
        </div>
      ))}
    </div>

    {footer && (
      <div className="mt-2 flex items-center justify-between gap-8 border-t border-[#e5e5e5] dark:border-white/[0.08] pt-1.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#9e9ea0] dark:text-white/40">
          {footer.label}
        </span>
        <span className="text-[12px] font-black leading-none tabular-nums text-[#111111] dark:text-white">
          {footer.value}
        </span>
      </div>
    )}
  </div>
);

export default ChartTooltip;
